<template>
  <div class="page-product-detail">
    <PageHeader/>

    <PageBanner
        title="产品详情"
        subtitle="了解产品详情，如有意向欢迎来电咨询洽谈。"
    />

    <div class="wrap detail-body">
      <div class="main-card" v-if="title">
        <!-- 左侧图片区 -->
        <div class="gallery">
          <div class="main-image-box">
            <img :src="images[currentImage]" alt="产品图片" class="main-image"/>
          </div>
          <div class="thumbnail-container" v-if="images.length > 1">
            <img
                v-for="(image, index) in images"
                :key="index"
                :src="image"
                alt="产品缩略图"
                class="thumbnail"
                :class="{ active: index === currentImage }"
                @click="currentImage = index"
            />
          </div>
        </div>

        <!-- 右侧信息区 -->
        <div class="product-info">
          <h1 class="p-title">{{ title }}</h1>
          <p class="p-subtitle" v-if="subtitle">{{ subtitle }}</p>
          <div class="price-box">
            <div class="price-line">
              <span class="label">价格</span>
              <span class="price">¥ {{ price }}</span>
            </div>
            <div class="guide-line" v-if="guidePrice">
              <span class="label">指导价</span>
              <span class="guide">¥ {{ guidePrice }}</span>
            </div>
          </div>
          <div class="contact-box">
            <span class="label">联系方式</span>
            <span class="contact">{{ contact }}</span>
          </div>
          <p class="tip">· 图片仅供参考，具体以实物为准；如需批量采购或合作洽谈，请直接电话联系。</p>
        </div>
      </div>

      <!-- 描述 / 参数 -->
      <div class="desc-card" v-if="title">
        <div class="desc-tabs">
          <div
              class="tab"
              :class="{ active: option === 'option1' }"
              @click="selectDescription('option1')"
          >产品描述</div>
          <div
              class="tab"
              :class="{ active: option === 'option2' }"
              @click="selectDescription('option2')"
          >参数说明</div>
        </div>

        <div v-if="option === 'option1'" id="content" class="rich-content" v-html="content"></div>
        <div v-if="option === 'option2'" class="parameters">
          <div class="param-row" v-for="(item, index) in parameters" :key="index">
            <div class="param-key">{{ item.key }}</div>
            <div class="param-value">{{ item.value }}</div>
          </div>
          <div class="param-empty" v-if="!parameters.length">暂无参数说明</div>
        </div>
      </div>

      <div class="loading" v-else>加载中…</div>
    </div>

    <FooterComponent/>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import PageHeader from "@/components/PageHeader.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import PageBanner from "@/components/PageBanner.vue";
import { noPermission } from "@/api/product";

const route = useRoute();
const id = ref(route.params.id);
const currentImage = ref(0);
const title = ref("");
const subtitle = ref("");
const option = ref("option1");
const content = ref("");
const images = ref([]);
const price = ref('');
const guidePrice = ref('');
const contact = ref('4000-567-200');
const parameters = ref([]);

const selectDescription = (selectedOption) => {
  option.value = selectedOption;
};

// 查询数据详情
const getNoPermission = (id) => {
  noPermission(Number(id.value)).then((response) => {
    if (!response || response.code !== 200) {
      return;
    }
    price.value = response.data.actualPrice ?? '面议';
    guidePrice.value = response.data.guidePrice ?? '';
    title.value = response.data.title;
    subtitle.value = response.data.subtitle;
    content.value = response.data.content;
    (response.data.atts || []).forEach(item => {
      images.value.push(item.attachmentPath);
    });
    (response.data.productParameters || []).forEach(item => {
      parameters.value.push({
        key: item.name,
        value: item.content
      });
    });
  });
};

onMounted(() => {
  getNoPermission(id);
});
</script>

<style scoped>
.page-product-detail {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
}

.detail-body {
  flex: 1;
  padding-top: 34px;
  padding-bottom: 56px;
}

.main-card {
  display: grid;
  grid-template-columns: 440px 1fr;
  gap: 36px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);
  padding: 28px;
}

/* ===== 图片区 ===== */
.gallery {
  min-width: 0;
}

.main-image-box {
  height: 400px;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  overflow: hidden;
  position: relative;
  background: #fff;
}

.main-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
  box-sizing: border-box;
}

.thumbnail-container {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.thumbnail {
  width: 68px;
  height: 68px;
  object-fit: contain;
  border: 2px solid var(--line);
  border-radius: var(--r-sm);
  background: #fff;
  padding: 3px;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color var(--fast);
}

.thumbnail.active,
.thumbnail:hover {
  border-color: var(--color-primary-500);
}

/* ===== 信息区 ===== */
.product-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.p-title {
  margin: 6px 0 0;
  font-size: 26px;
  line-height: 1.45;
  color: var(--text-1);
}

.p-subtitle {
  margin: 10px 0 0;
  font-size: 15px;
  color: var(--text-3);
}

.price-box {
  margin-top: 26px;
  background: linear-gradient(120deg, #fdf3f3, #fff8f2);
  border: 1px solid #f5e3d8;
  border-radius: var(--r-md);
  padding: 16px 20px;
}

.price-line,
.guide-line {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.price-line .price {
  font-size: 30px;
  font-weight: 700;
  color: #d03050;
}

.guide-line {
  margin-top: 6px;
}

.guide {
  font-size: 15px;
  color: var(--text-3);
  text-decoration: line-through;
}

.price-line .label,
.guide-line .label {
  font-size: 13px;
  color: var(--text-3);
  flex: none;
}

.contact-box {
  margin-top: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
}

.contact-box .label {
  font-size: 13px;
  color: var(--text-3);
}

.contact {
  color: var(--color-primary-600);
  font-weight: 600;
  letter-spacing: 1px;
}

.tip {
  margin-top: auto;
  padding-top: 24px;
  font-size: 12.5px;
  color: #a8b3c7;
  line-height: 1.8;
}

/* ===== 描述/参数 ===== */
.desc-card {
  margin-top: 20px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);
  padding: 0 28px 28px;
}

.desc-tabs {
  display: flex;
  gap: 32px;
  border-bottom: 1px solid var(--line);
}

.tab {
  padding: 16px 4px;
  font-size: 15.5px;
  color: var(--text-2);
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;
  transition: color var(--fast);
}

.tab:hover {
  color: var(--color-primary-500);
}

.tab.active {
  color: var(--color-primary-500);
  font-weight: 600;
  border-bottom-color: var(--color-primary-500);
}

.rich-content {
  padding-top: 22px;
  font-size: 15px;
  color: var(--text-2);
  line-height: 1.9;
  overflow: hidden;
}

.rich-content :deep(img) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 14px auto;
  border-radius: var(--r-sm);
}

.rich-content :deep(p) {
  margin: 10px 0;
}

/* 参数表 */
.parameters {
  padding-top: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  border-bottom: 1px solid var(--line);
}

.param-key {
  padding: 13px 16px;
  background: #f7f9fd;
  font-size: 14px;
  color: var(--text-2);
  border-right: 1px solid var(--line);
}

.param-value {
  padding: 13px 16px;
  font-size: 14px;
  color: var(--text-1);
}

.param-empty {
  text-align: center;
  color: var(--text-3);
  padding: 32px 0;
}

.loading {
  text-align: center;
  color: var(--text-3);
  padding: 80px 0;
}

@media (max-width: 900px) {
  .main-card {
    grid-template-columns: 1fr;
    gap: 22px;
  }
  .main-image-box {
    height: 300px;
  }
  .param-row {
    grid-template-columns: 130px 1fr;
  }
}
</style>
